/**
 * Tenancy module contracts (ADR-06, docs/02 §Tenancy).
 *
 * The Org/Tenancy split in types: a TENANT ORG is the business (one row per
 * company, portfolio-wide, duplicate-guarded like properties); a TENANCY is
 * that business occupying ONE property, and the lease hangs off the tenancy.
 * The Beta modeled all of this as one table and got a company per occupancy —
 * the duplicate-company defect this split exists to kill.
 */

import { z } from 'zod';

import {
  addressInlineSchema,
  phoneEditableSchema,
  type AddressRow,
  type PhoneRow,
} from './address.ts';
import { partialUpdateSchema } from './partial-update.ts';
import type { DuplicateMatch } from './properties.ts';
import { DEFAULT_PAGE_SIZE } from './table-layouts.ts';

// ---------------------------------------------------------------------------
// Tenant organizations
// ---------------------------------------------------------------------------

export const tenantOrgWriteSchema = z.object({
  name: z.string().trim().min(1).max(200),
  industry: z.string().trim().max(120).nullish(),
  website: z.union([z.literal(''), z.url()]).nullish(),
  primaryEmail: z.union([z.literal(''), z.email()]).nullish(),
  // Addresses and phone numbers are SUB-RESOURCES as of 0040 — see
  // contracts/address.ts and docs/02. They are not fields here because a
  // company may have any number of each, and each carries its own purpose.
  /** ADR-37 — the Important Details band, same field name on every entity. */
  criticalInformation: z.string().trim().max(4000).nullish(),
  notes: z.string().trim().max(4000).nullish(),
});
export type TenantOrgWrite = z.infer<typeof tenantOrgWriteSchema>;

export const tenantOrgCreateSchema = tenantOrgWriteSchema
  .extend({
    /** The duplicate interstitial's consent, exactly as properties do it. */
    createAnyway: z.boolean().default(false),
    createAnywayReason: z.string().trim().max(500).optional(),
  })
  // STRICT (see partialUpdateSchema): 0040 moved the phone and addresses off.
  .strict();
export type TenantOrgCreate = z.infer<typeof tenantOrgCreateSchema>;

export const tenantOrgUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(tenantOrgWriteSchema),
});

export const tenantOrgListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(['active', 'inactive', 'all']).default('active'),
  sort: z.enum(['name', 'industry', 'status', 'updated_at']).default('name'),
  dir: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  /**
   * Rows per page (X-6.3). Coerced to a legal size by the service rather than
   * rejected here, because the value arrives from a STORED preference that can
   * outlive the set it was legal in — and a 400 would then break the whole list
   * for whoever held the retired value. Absent means DEFAULT_PAGE_SIZE.
   */
  pageSize: z.coerce.number().int().optional(),
});
export type TenantOrgListQuery = z.infer<typeof tenantOrgListQuerySchema>;

/**
 * The DEFAULT page size for this list. One number for the whole app since
 * X-6.3 made the size a per-user choice — a second literal here would be a
 * second answer to "how many rows does a page hold when nobody chose".
 */
export const TENANT_ORG_PAGE_SIZE = DEFAULT_PAGE_SIZE;

export interface TenantOrgRow {
  readonly id: string;
  readonly name: string;
  readonly industry: string | null;
  readonly status: 'active' | 'inactive';
  /** Active tenancies, so the list answers "where are they" at a glance. */
  readonly activeTenancies: number;
  readonly updatedAt: string;
  readonly version: number;
}

export interface TenancySummary {
  readonly id: string;
  readonly propertyId: string;
  /**
   * What OTHER records call this property (0068, docs/17 3D.5): its street
   * line, falling back to its name. NOT `properties.name` — most Yardi names
   * are the owning legal entity (`TKC CIII, LLC`) and nobody asks for a
   * building by its LLC. The property's own page still shows its name.
   */
  readonly propertyLabel: string;
  readonly status: 'pending' | 'active' | 'inactive';
  readonly moveIn: string | null;
  readonly moveOut: string | null;
  readonly spaceLabels: readonly string[];
}

export interface TenantContactRow {
  readonly id: string;
  readonly tenancyId: string | null;
  readonly name: string;
  readonly title: string | null;
  readonly email: string | null;
  readonly notes: string | null;
  readonly isPrimary: boolean;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  /** 0040: any number of numbers, each labelled with its purpose. */
  readonly phones: readonly PhoneRow[];
  readonly addresses: readonly AddressRow[];
}

export interface TenantOrgDetail {
  readonly id: string;
  readonly name: string;
  readonly industry: string | null;
  readonly website: string | null;
  readonly primaryEmail: string | null;
  readonly criticalInformation: string | null;
  readonly notes: string | null;
  readonly status: 'active' | 'inactive';
  readonly version: number;
  readonly updatedAt: string;
  /**
   * Whether the caller may write this company's contacts and contact detail:
   * the org's own scoped rule (0036/0040/0095), not the `tenants.edit` key alone.
   */
  readonly canEdit: boolean;
  readonly tenancies: readonly TenancySummary[];
  readonly contacts: readonly TenantContactRow[];
  readonly addresses: readonly AddressRow[];
  readonly phones: readonly PhoneRow[];
}

/** The create endpoint's 409 payload — same shape properties use. */
export interface TenantOrgDuplicateReview {
  readonly code: 'DUPLICATE_REVIEW';
  readonly message: string;
  readonly duplicates: readonly DuplicateMatch[];
}

// ---------------------------------------------------------------------------
// Tenancies
// ---------------------------------------------------------------------------

export const tenancyWriteSchema = z.object({
  status: z.enum(['pending', 'active', 'inactive']).default('pending'),
  moveIn: z.iso.date().nullish(),
  moveOut: z.iso.date().nullish(),
  notes: z.string().trim().max(4000).nullish(),
  /**
   * Where this tenant is kept in two other systems (0067).
   *
   * On the TENANCY because the Yardi folder is per property — one organization
   * with locations at two buildings has two folders, and 23 of the snapshot's
   * 411 do. `''` is accepted from a cleared field and means null, the same
   * shape `tkcWorkUrl` uses; the CHECK refuses an empty string in the column.
   */
  yardiSharePointUrl: z.union([z.literal(''), z.url().max(1000)]).nullish(),
  /** The system is not chosen yet (Matt, 2026-08-28: "yet to be determined"). */
  propertyManagementUrl: z.union([z.literal(''), z.url().max(1000)]).nullish(),
  /** Full replace, like every sub-list on a write (ADR-35). */
  spaceIds: z.array(z.uuid()).max(200).default([]),
});
export type TenancyWrite = z.infer<typeof tenancyWriteSchema>;

export const tenancyCreateSchema = tenancyWriteSchema.extend({
  orgId: z.uuid(),
  propertyId: z.uuid(),
});
export type TenancyCreate = z.infer<typeof tenancyCreateSchema>;

export const tenancyUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(tenancyWriteSchema),
});

export interface TenancySpaceRow {
  readonly spaceId: string;
  readonly label: string;
  readonly sqFt: number | null;
  readonly floor: string | null;
}

export interface LeaseRow {
  readonly id: string;
  readonly status: 'draft' | 'active' | 'superseded' | 'inactive';
  readonly commencement: string | null;
  readonly expiration: string | null;
  readonly executedDate: string | null;
  /**
   * Rentable square feet demised by this lease (0044).
   *
   * On the LEASE because the demised premises is a lease term (Matt
   * 2026-08-25): an expansion is an amendment, which supersedes the lease, so
   * the figure always matches the dates it applies to. NOT the sum of the
   * suites' `sqFt` — suites get renumbered, and rentable area carries a
   * common-space load factor, which is the number rent is actually computed on.
   */
  readonly rentableSqFt: number | null;
  readonly notes: string | null;
  readonly version: number;
  readonly currentRevisionId: string | null;
  /**
   * Who this lease is with, by role (0069, docs/17 3B.2).
   *
   * The same people the TENANT already carries — `tenant_contacts` rows, joined,
   * never copied. Matt, 2026-08-28: *"since tenants have contact, one or more
   * contacts can be associated with leases as well"*. A person may hold two
   * roles on one lease (billing and notices is the common pair), which is two
   * rows here and reads correctly on screen.
   */
  readonly contacts: readonly LeaseContactRow[];
  /**
   * THIS lease's current abstract revision, or null if it has none.
   *
   * Distinct from `TenancyDetail.currentAbstract`, which is the ACTIVE lease's
   * (or the newest one's) and is what the overview card summarises. The
   * distinction is not academic: the lease form seeds its Abstract tab from
   * here, and seeding a superseded lease's tab from the ACTIVE lease's revision
   * — or from nothing — would let one edit write a revision that silently
   * dropped every term that lease actually recorded.
   */
  readonly abstract: LeaseAbstract | null;
}

export interface LeaseAbstract {
  readonly id: string;
  readonly leaseId: string;
  readonly revisionNo: number;
  readonly baseRentCents: number | null;
  readonly securityDepositCents: number | null;
  readonly escalations: readonly unknown[];
  /**
   * Typed now that there is a shape to type (2026-08-25). It stayed `unknown[]`
   * while nothing wrote it; the editor validates every row against
   * leaseOptionSchema, and every row that has ever existed carries `kind`,
   * because the only prior value of this column was the empty array.
   */
  readonly options: readonly LeaseOption[];
  readonly termNotes: string | null;
  readonly keyClauses: string | null;
  readonly notes: string | null;
  /** How operating expenses are handled — NNN, gross, base year (0069). */
  readonly operatingExpenseTreatment: string | null;
  readonly camNotes: string | null;
  readonly insuranceRequirements: string | null;
  /** Tenant improvement allowance, integer cents (ADR-13). */
  readonly tiAllowanceCents: number | null;
  /** BASIS POINTS. 6% is 600 — integer, for the reason money is cents. */
  readonly percentageRentBps: number | null;
  readonly percentageRentNotes: string | null;
  readonly guarantor: string | null;
  readonly createdAt: string;
  readonly createdByName: string | null;
}

export interface TenancyDetail {
  readonly id: string;
  readonly orgId: string;
  readonly orgName: string;
  readonly orgCriticalInformation: string | null;
  readonly propertyId: string;
  /**
   * What OTHER records call this property (0068, docs/17 3D.5): its street
   * line, falling back to its name. NOT `properties.name` — most Yardi names
   * are the owning legal entity (`TKC CIII, LLC`) and nobody asks for a
   * building by its LLC. The property's own page still shows its name.
   */
  readonly propertyLabel: string;
  readonly status: 'pending' | 'active' | 'inactive';
  readonly moveIn: string | null;
  readonly moveOut: string | null;
  readonly notes: string | null;
  /** Where this tenant lives in two other systems (0067). */
  readonly yardiSharePointUrl: string | null;
  readonly propertyManagementUrl: string | null;
  readonly version: number;
  readonly canEdit: boolean;
  readonly canManageLeases: boolean;
  readonly spaces: readonly TenancySpaceRow[];
  readonly leases: readonly LeaseRow[];
  readonly currentAbstract: LeaseAbstract | null;
  readonly contacts: readonly TenantContactRow[];
}

// ---------------------------------------------------------------------------
// Leases & abstract revisions
// ---------------------------------------------------------------------------

export const leaseWriteSchema = z.object({
  commencement: z.iso.date().nullish(),
  expiration: z.iso.date().nullish(),
  executedDate: z.iso.date().nullish(),
  /** Positive or absent; a zero would claim they lease nothing (0044). */
  rentableSqFt: z.number().int().positive().max(10_000_000).nullish(),
  notes: z.string().trim().max(4000).nullish(),
});
export type LeaseWrite = z.infer<typeof leaseWriteSchema>;

export const leaseUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(leaseWriteSchema),
});

/** One escalation step. Kept simple; the abstract editor owns the shape. */
export const escalationSchema = z.object({
  effective: z.iso.date(),
  baseRentCents: z.number().int().min(0),
  note: z.string().trim().max(500).optional(),
});

/**
 * One lease option — a right the tenant or the landlord may exercise.
 *
 * `deadline` was added 2026-08-25 with the editor that finally offered these
 * (docs/11 X-9): the column was jsonb and hardcoded to `[]`, and the Key clauses
 * hint told people to type "renewals, termination options, anything with a
 * deadline" as prose instead. Prose cannot become an action item, which is the
 * entire point of holding the date as data. The expirations view does not read
 * it yet — that is the derived-dates work — but the date is now recorded rather
 * than buried in a paragraph.
 */
export const leaseOptionSchema = z.object({
  kind: z.enum(['renewal', 'termination', 'expansion', 'other']),
  /** The date by which it must be exercised, when the lease names one. */
  deadline: z.iso.date().optional(),
  notice: z.string().trim().max(500).optional(),
  terms: z.string().trim().max(2000).optional(),
});
export type LeaseOption = z.infer<typeof leaseOptionSchema>;

/** Reader-facing labels for the option kinds — never the raw key on screen. */
export const LEASE_OPTION_LABEL: Readonly<Record<LeaseOption['kind'], string>> = {
  renewal: 'Renewal',
  termination: 'Termination',
  expansion: 'Expansion',
  other: 'Other',
};

/**
 * The abstract a real lease abstract carries.
 *
 * Matt, 2026-08-28, asked for the common CRE fields on top of rent and deposit:
 * *"Add the common CRE fields too"* — CAM / operating-expense treatment,
 * insurance requirements, TI allowance, percentage rent, guarantor. They live on
 * the REVISION like everything else here (0069): an amendment never overwrites
 * what the last one said, and a field that skipped the revision would be the one
 * fact about a lease with no history.
 */
export const abstractRevisionSchema = z.object({
  /**
   * The lease version the editor opened (docs/11 F-49, mailbox RM-007). The
   * abstract-only save path bypasses the versioned lease PATCH entirely, so
   * without this the current abstract can move backwards silently.
   */
  version: z.number().int().positive(),
  baseRentCents: z.number().int().min(0).nullish(),
  securityDepositCents: z.number().int().min(0).nullish(),
  escalations: z.array(escalationSchema).max(100).default([]),
  options: z.array(leaseOptionSchema).max(50).default([]),
  termNotes: z.string().trim().max(4000).nullish(),
  keyClauses: z.string().trim().max(8000).nullish(),
  notes: z.string().trim().max(4000).nullish(),
  /**
   * Free text on purpose: the vocabulary varies by lease ("NNN", "modified
   * gross, 2026 base year", "full service"), and a wrong enum value is worse
   * than a sentence somebody can read.
   */
  operatingExpenseTreatment: z.string().trim().max(500).nullish(),
  camNotes: z.string().trim().max(4000).nullish(),
  insuranceRequirements: z.string().trim().max(4000).nullish(),
  /** Integer cents (ADR-13), like every other amount in this app. */
  tiAllowanceCents: z.number().int().min(0).nullish(),
  /**
   * Percentage rent in BASIS POINTS. 6% is 600. Integer for the same reason
   * money is cents — a float percentage is a rounding error with a decimal
   * point in it. Capped at 10000 (100%), which the column CHECKs too.
   */
  percentageRentBps: z.number().int().min(0).max(10_000).nullish(),
  percentageRentNotes: z.string().trim().max(2000).nullish(),
  guarantor: z.string().trim().max(500).nullish(),
});

/** One basis point is a hundredth of a percent — the conversion, in one place. */
export function bpsToPercent(bps: number): number {
  return bps / 100;
}
export function percentToBps(percent: number): number {
  return Math.round(percent * 100);
}
export type AbstractRevisionWrite = z.infer<typeof abstractRevisionSchema>;

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

export const tenantContactWriteSchema = z.object({
  tenancyId: z.uuid().nullish(),
  name: z.string().trim().min(1).max(120),
  title: z.string().trim().max(120).nullish(),
  email: z.union([z.literal(''), z.email()]).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  isPrimary: z.boolean().default(false),
  /**
   * ONE phone and ONE address, typed while the contact is being created
   * (2026-08-25). Matt, twice: "contacts should have names, titles, email,
   * addresses, phone numbers - why hasnt this been completed?"
   *
   * They remain SUB-RESOURCES — any number of each, with their own purposes and
   * primary flags, on the contact's cards (0040). What changed is that entering
   * the first one no longer requires saving, finding the row and expanding it:
   * a property manager taking a name and a number off a phone call types both.
   * The same shapes the cards use, so there is one standard, not a second one.
   */
  phone: phoneEditableSchema.optional(),
  address: addressInlineSchema.optional(),
});
export type TenantContactWrite = z.infer<typeof tenantContactWriteSchema>;

export const tenantContactUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(tenantContactWriteSchema),
});

// ---------------------------------------------------------------------------
// Lease contacts (0069, docs/17 3B.2)
// ---------------------------------------------------------------------------

/**
 * A role a contact holds ON a lease — admin-managed like every other taxonomy
 * in this app (Matt, 2026-08-24: "needs to be managed in admin panel"), which
 * is why it is a row and not a TypeScript union.
 */
export interface LeaseContactRoleRow {
  readonly id: string;
  readonly systemKey: string;
  readonly name: string;
}

/**
 * One person named on one lease, in one role.
 *
 * The person is a `tenant_contacts` row the TENANT already owns — this is a
 * join, never a copy. Copying contacts is how two records of one person drift
 * apart, which is the defect 0040 was written to end.
 */
export interface LeaseContactRow {
  readonly id: string;
  readonly tenantContactId: string;
  readonly name: string;
  readonly title: string | null;
  readonly email: string | null;
  readonly roleId: string;
  readonly roleKey: string;
  readonly roleName: string;
  /** So the form can warn before saving a link to somebody deactivated. */
  readonly contactStatus: 'active' | 'inactive';
  /**
   * Whether the ROLE is still offered (CR-024). A link keeps naming a role
   * retired after it was made; the editor keeps such a row as itself — keep it
   * or remove it — because the role is no longer in its picker.
   */
  readonly roleActive: boolean;
}

/**
 * Full replace, like every sub-list on a write (ADR-35). The whole set is sent;
 * the service computes the difference so the audit entry names who was added
 * and who was dropped rather than "contacts changed".
 */
/**
 * The full contact set for a lease, bound to the lease version the editor
 * opened (docs/11 F-49, mailbox RM-010). Without the token this replacement
 * cannot tell a link the editor never saw from one they deliberately removed.
 */
export const leaseContactsWriteSchema = z.object({
  version: z.number().int().positive(),
  links: z
    .array(
      z.object({
        tenantContactId: z.uuid(),
        roleId: z.uuid(),
      }),
    )
    .max(50)
    .default([]),
});
export type LeaseContactsWrite = z.infer<typeof leaseContactsWriteSchema>;
